import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userUpdatePassword } from '../../redux/authSlice';
import { resetAdminPassword } from '../../redux/adminSlice';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Eye, EyeOff, Check, AlertTriangle, KeyRound, Info } from 'lucide-react';
import useAxiosAuth from '../../hooks/useAxiosAuth';

// `managed` = a super-admin is resetting ANOTHER admin's password. A RESET
// NEVER PUTS A PASSWORD IN A MAIL OR ON A SCREEN (owner, 2 Oct 2026): nothing
// is typed or shown — the server (PUT /admins/:id/password) makes the current
// password stop working and tells the admin to set a new one with Forgot
// password. Otherwise this is the self-service change flow.
function ManagedPasswordReset({ admin }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mutating = useSelector((state) => state.admin?.mutating);
    const [busy, setBusy] = useState(false);
    const working = mutating || busy;

    const handleReset = async () => {
        const result = await Swal.fire({
            title: t('passwordReset.confirm_title'),
            text: t('passwordReset.confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('passwordReset.confirm'),
            cancelButtonText: t('passwordReset.cancel'),
        });
        if (!result.isConfirmed) return;
        setBusy(true);
        try {
            const res = await dispatch(resetAdminPassword({ adminId: admin.adminId })).unwrap();
            toast.success(res?.data?.noticeDelivered === false
                ? t('passwordReset.done_not_sent')
                : t('passwordReset.done_sent'));
        } catch (err) {
            toast.warn((typeof err?.error === 'string' ? err.error : null) || t('passwordReset.error'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-password-container" className="mt-4">
            <div data-testid="admin-password-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 data-testid="admin-password-heading" className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('passwordReset.title')}
                </h2>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 max-w-2xl">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                        <KeyRound className="w-5 h-5" />
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{t('passwordReset.confirm_text')}</p>
                </div>
                <div className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5">
                    <Info className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-slate-500">{t('passwordReset.sessions_note')}</p>
                </div>
                <div className="flex justify-end mt-5">
                    <button
                        type="button"
                        data-testid="admin-password-reset-button"
                        onClick={handleReset}
                        disabled={working}
                        className={`px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg font-semibold text-sm ${working ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-xl cursor-pointer'}`}
                    >
                        {working ? t('passwordReset.resetting') : t('passwordReset.confirm')}
                    </button>
                </div>
            </div>
        </div>
    );
}

function AdminPasswordTab({ admin, managed = false }) {
    if (managed) return <ManagedPasswordReset admin={admin} />;
    return <SelfPasswordChange admin={admin} />;
}

// The signed-in admin changing their OWN password (current one required).
function SelfPasswordChange({ admin, managed = false }) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const { t } = useTranslation(['doctorpassword', 'common']);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({ currentPassword: '', password: '', confirmPassword: '' });

    const dispatch = useDispatch();
    const axiosInstance = useAxiosAuth();
    const { isLoading: authLoading } = useSelector((state) => state.auth);
    const mutating = useSelector((state) => state.admin?.mutating);
    // Busy flag for whichever flow is active.
    const isLoading = managed ? mutating : authLoading;

    const validatePassword = (pwd) => {
        const minLength = pwd.length >= 8;
        const maxLength = pwd.length <= 25;
        const hasUpperCase = /[A-Z]/.test(pwd);
        const hasLowerCase = /[a-z]/.test(pwd);
        const hasNumber = /[0-9]/.test(pwd);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

        if (!minLength) return t('doctorpassword.text1', { ns: 'doctorpassword' });
        if (!maxLength) return t('validation.password_too_long_25', { ns: 'common' });
        if (!hasUpperCase) return t('doctorpassword.text2', { ns: 'doctorpassword' });
        if (!hasLowerCase) return t('doctorpassword.text3', { ns: 'doctorpassword' });
        if (!hasNumber) return t('doctorpassword.text4', { ns: 'doctorpassword' });
        if (!hasSpecialChar) return t('doctorpassword.text5', { ns: 'doctorpassword' });
        return '';
    };

    const getPasswordStrength = (pwd) => {
        if (!pwd) return '';
        const minLength = pwd.length >= 8;
        const maxLength = pwd.length <= 25;
        const hasUpperCase = /[A-Z]/.test(pwd);
        const hasLowerCase = /[a-z]/.test(pwd);
        const hasNumber = /[0-9]/.test(pwd);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

        // Password is strong if it meets all requirements
        if (minLength && maxLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar) {
            return t('doctorpassword.strong', { ns: 'doctorpassword' });
        }
        return t('doctorpassword.weak', { ns: 'doctorpassword' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Client-side validation. Managed reset skips the current-password checks.
        const currentPasswordError = managed
            ? ''
            : (!currentPassword ? t('doctorpassword.current_required', { ns: 'doctorpassword' }) : '');
        const passwordError = validatePassword(password);
        const sameAsCurrentError = !managed && password && currentPassword && password === currentPassword
            ? t('doctorpassword.same_as_current', { ns: 'doctorpassword' })
            : '';
        const confirmPasswordError =
            password !== confirmPassword
                ? t('doctorpassword.notmatch', { ns: 'doctorpassword' })
                : '';

        if (currentPasswordError || passwordError || sameAsCurrentError || confirmPasswordError) {
            setErrors({
                currentPassword: currentPasswordError,
                password: sameAsCurrentError || passwordError,
                confirmPassword: confirmPasswordError,
            });
            return;
        }

        // SweetAlert confirmation BEFORE API hit
        const result = await Swal.fire({
            title: t('doctorpassword.confirm_title'),
            text: t('doctorpassword.confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#d33',
            confirmButtonText: t('doctorpassword.yes'),
            cancelButtonText: t('doctorpassword.no'),
        });

        if (result.isConfirmed) {
            try {
                const adminId = admin.id || admin.adminId || localStorage.getItem('ID');
                await dispatch(
                    userUpdatePassword({
                        docId: adminId,
                        currentPassword,
                        newPassword: password,
                        confirmPassword,
                        axiosInstance,
                    })
                ).unwrap();

                // Reset form
                setCurrentPassword('');
                setPassword('');
                setConfirmPassword('');
                setErrors({ currentPassword: '', password: '', confirmPassword: '' });

                // Success alert
                toast.success(t('doctorpassword.success_text'));
            } catch (err) {
                // Error alert
                const errorMessage = (managed && typeof err?.error === 'string' ? err.error : err) || t('doctorpassword.error_text');
                toast.warn(errorMessage);
            }
        }
    };

    const handleCancel = () => {
        setCurrentPassword('');
        setPassword('');
        setConfirmPassword('');
        setErrors({ currentPassword: '', password: '', confirmPassword: '' });
    };

    return (
        <div data-testid="admin-password-container" className="mt-4">
            {/* Header Row */}
            <div data-testid="admin-password-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 data-testid="admin-password-heading" className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('doctorpassword.heading', { ns: 'doctorpassword' })}
                </h2>
            </div>

            <div data-testid="admin-password-fields-grid" className={`grid grid-cols-1 gap-6 w-full ${managed ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                {!managed && (
                <div className="relative">
                    <label data-testid="admin-profile-current-password-label" htmlFor="input-current-password" className="block text-sm font-semibold text-gray-600 mb-2">
                        {t('doctorpassword.current', { ns: 'doctorpassword' })} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            data-testid="admin-profile-current-password-input"
                            id="input-current-password"
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => {
                                const newCurrentPassword = e.target.value;
                                setCurrentPassword(newCurrentPassword);
                                if (errors.currentPassword) {
                                    setErrors({ ...errors, currentPassword: '' });
                                }
                                // Check if new password is same as current password
                                if (password && password === newCurrentPassword) {
                                    setErrors(prev => ({
                                        ...prev,
                                        password: t('doctorpassword.same_as_current', { ns: 'doctorpassword' })
                                    }));
                                } else if (errors.password && password !== newCurrentPassword) {
                                    // Clear the error if passwords are now different
                                    const passwordError = validatePassword(password);
                                    setErrors(prev => ({
                                        ...prev,
                                        password: passwordError
                                    }));
                                }
                            }}
                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.currentPassword ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('doctorpassword.currenttext', { ns: 'doctorpassword' })}
                            disabled={isLoading}
                        />
                        <button
                            data-testid="admin-profile-current-password-toggle"
                            id="btn-toggle-current-password"
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer text-gray-400 hover:text-[#4ABDBD] transition-colors duration-300"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            disabled={isLoading}
                        >
                            {showCurrentPassword ? (
                                <Eye data-testid="admin-profile-current-password-toggle-eye-icon" className="h-5 w-5" />
                            ) : (
                                <EyeOff data-testid="admin-profile-current-password-toggle-eyeoff-icon" className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                    {errors.currentPassword && (
                        <p data-testid="admin-profile-current-password-error" className="mt-2 text-xs text-red-500 font-medium">{errors.currentPassword}</p>
                    )}
                </div>
                )}

                <div className="relative">
                    <label data-testid="admin-profile-new-password-label" htmlFor="input-password" className="block text-sm font-semibold text-gray-600 mb-2">
                        {t('doctorpassword.new', { ns: 'doctorpassword' })} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            data-testid="admin-profile-new-password-input"
                            id="input-password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => {
                                const newPassword = e.target.value;
                                
                                // Enforce max length
                                if (newPassword.length > 25) {
                                    return;
                                }
                                
                                setPassword(newPassword);
                                
                                // Clear password error if exists
                                if (errors.password) {
                                    setErrors({ ...errors, password: '' });
                                }
                                
                                // Check if new password is same as current password
                                if (currentPassword && newPassword === currentPassword) {
                                    setErrors(prev => ({
                                        ...prev,
                                        password: t('doctorpassword.same_as_current', { ns: 'doctorpassword' })
                                    }));
                                } else if (errors.password && newPassword !== currentPassword) {
                                    // Validate password if it's different from current
                                    const passwordError = validatePassword(newPassword);
                                    setErrors(prev => ({
                                        ...prev,
                                        password: passwordError
                                    }));
                                }
                                
                                // Check confirm password match if confirm password has value
                                if (confirmPassword && newPassword !== confirmPassword) {
                                    setErrors(prev => ({
                                        ...prev,
                                        confirmPassword: t('doctorpassword.notmatch', { ns: 'doctorpassword' })
                                    }));
                                } else if (confirmPassword && newPassword === confirmPassword) {
                                    setErrors(prev => ({
                                        ...prev,
                                        confirmPassword: ''
                                    }));
                                }
                            }}
                            maxLength={25}
                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('doctorpassword.newtext', { ns: 'doctorpassword' })}
                            disabled={isLoading}
                        />
                        <button
                            data-testid="admin-profile-new-password-toggle"
                            id="btn-toggle-password"
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer text-gray-400 hover:text-[#4ABDBD] transition-colors duration-300"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={isLoading}
                        >
                            {showPassword ? (
                                <Eye data-testid="admin-profile-new-password-toggle-eye-icon" className="h-5 w-5" />
                            ) : (
                                <EyeOff data-testid="admin-profile-new-password-toggle-eyeoff-icon" className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                    {errors.password && (
                        <p data-testid="admin-profile-new-password-error" className="mt-2 text-xs text-red-500 font-medium">{errors.password}</p>
                    )}
                    {password && !errors.password && (
                        <p data-testid="admin-profile-password-strength-message" className={`text-xs mt-1 flex items-center gap-1 ${
                            getPasswordStrength(password) === t('doctorpassword.strong', { ns: 'doctorpassword' })
                                ? 'text-green-600'
                                : 'text-amber-600'
                        }`}>
                            {getPasswordStrength(password) === t('doctorpassword.strong', { ns: 'doctorpassword' })
                                ? <Check className="w-3 h-3" />
                                : <AlertTriangle className="w-3 h-3" />}
                            {t('doctorpassword.password_strength', { ns: 'doctorpassword' })} {getPasswordStrength(password)}
                        </p>
                    )}
                </div>

                <div className="relative">
                    <label data-testid="admin-profile-confirm-password-label" htmlFor="input-confirm-password" className="block text-sm font-semibold text-gray-600 mb-2">
                        {t('doctorpassword.confirm', { ns: 'doctorpassword' })} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input
                            data-testid="admin-profile-confirm-password-input"
                            id="input-confirm-password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => {
                                const newConfirmPassword = e.target.value;
                                
                                // Enforce max length
                                if (newConfirmPassword.length > 25) {
                                    return;
                                }
                                
                                setConfirmPassword(newConfirmPassword);
                                
                                // Clear error if exists
                                if (errors.confirmPassword) {
                                    setErrors({ ...errors, confirmPassword: '' });
                                }
                                
                                // Check if confirm password matches new password
                                if (password && newConfirmPassword !== password) {
                                    setErrors(prev => ({
                                        ...prev,
                                        confirmPassword: t('doctorpassword.notmatch', { ns: 'doctorpassword' })
                                    }));
                                } else if (password && newConfirmPassword === password) {
                                    setErrors(prev => ({
                                        ...prev,
                                        confirmPassword: ''
                                    }));
                                }
                            }}
                            maxLength={25}
                            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${errors.confirmPassword ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-[#4ABDBD] focus:border-[#4ABDBD]'}`}
                            placeholder={t('doctorpassword.confirmtext', { ns: 'doctorpassword' })}
                            disabled={isLoading}
                        />
                        <button
                            data-testid="admin-profile-confirm-password-toggle"
                            id="btn-toggle-confirm-password"
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer text-gray-400 hover:text-[#4ABDBD] transition-colors duration-300"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            disabled={isLoading}
                        >
                            {showConfirmPassword ? (
                                <Eye data-testid="admin-profile-confirm-password-toggle-eye-icon" className="h-5 w-5" />
                            ) : (
                                <EyeOff data-testid="admin-profile-confirm-password-toggle-eyeoff-icon" className="h-5 w-5" />
                            )}
                        </button>
                    </div>
                    {errors.confirmPassword && (
                        <p data-testid="admin-profile-confirm-password-error" className="mt-2 text-xs text-red-500 font-medium">{errors.confirmPassword}</p>
                    )}
                </div>
            </div>

            <div data-testid="admin-password-actions" className="flex justify-center gap-4 mt-8">
                <button
                    data-testid="admin-profile-password-save-button"
                    id="btn-update-password"
                    type="button"
                    onClick={handleSubmit}
                    className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold relative overflow-hidden text-sm ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
                    disabled={isLoading}
                >
                    <span data-testid="update-text">
                        {isLoading ? t('doctorpassword.updating', { ns: 'doctorpassword' }) : t('doctorpassword.update', { ns: 'doctorpassword' })}
                    </span>
                </button>
                <button
                    data-testid="admin-profile-password-cancel-button"
                    id="btn-cancel"
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                    disabled={isLoading}
                >
                    {t('doctorpassword.cancel', { ns: 'doctorpassword' })}
                </button>
            </div>
        </div>
    );
}

export default AdminPasswordTab;

